import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import Keycloak from "keycloak-js";
import keycloakInstance from "../../../config/keycloak";
import { authService } from "@/services/authService";
import { profileService } from "@/services/profileService";
import { OrganizationSelectorModal } from "./OrganizationSelectorModal";
import { useSettingsStore } from "@/store/settingsStore";
import { useThemeStore } from "@/store/themeStore";
import {
  handleSessionExpired,
  registerSessionClearedHandler,
  registerSessionLoginHandler,
} from "@/lib/authSession";

interface KeycloakContextType {
  authenticated: boolean;
  keycloak: Keycloak | null;
  logout: (redirectPath?: string) => void;
  token: string | undefined;
  profileCompleted: boolean;
}

const KeycloakContext = createContext<KeycloakContextType | undefined>(undefined);

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) throw new Error("useKeycloak must be used within KeycloakProvider");
  return context;
};

let isKeycloakInitialized = false;

function getPersistedOrganization(
  organizations: { id: string; name: string }[],
): { id: string; name: string } | null {
  const selectedLegalEntityId = useSettingsStore.getState().legalEntityId;
  if (!selectedLegalEntityId) return null;
  return organizations.find((organization) => organization.id === selectedLegalEntityId) ?? null;
}

export const KeycloakProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [profileCompleted, setProfileCompleted] = useState(true); // Default to true to avoid flicker
  const [pendingOrganizations, setPendingOrganizations] = useState<{ id: string; name: string }[]>([]);
  const [resolvePendingOrg, setResolvePendingOrg] = useState<((org: { id: string; name: string }) => void) | null>(null);

  useEffect(() => {
    registerSessionLoginHandler(() => {
      void keycloakInstance.login({ redirectUri: window.location.href });
    });
    registerSessionClearedHandler(() => {
      setAuthenticated(false);
      setToken(undefined);
    });
  }, []);

  useEffect(() => {
    if (isKeycloakInitialized) return;
    isKeycloakInitialized = true;

    let cleanup: (() => void) | undefined;

    const initKeycloak = async () => {
      const kc = keycloakInstance;
      try {
        const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === "true";
        if (useMockAuth) {
          const mockEmail = import.meta.env.VITE_MOCK_USER_EMAIL || "test@bridgeai.co.uk";
          console.warn("MOCK AUTH ENABLED: Bypassing Keycloak for email", mockEmail);
          
          const mockKc = {
            tokenParsed: { email: mockEmail },
            token: "mock-token",
            updateToken: async () => true,
            logout: () => { window.location.reload(); }
          } as unknown as Keycloak;
          
          // Apply the account's theme first, before anything else renders.
          useThemeStore.getState().setAccount(mockEmail);
          setKeycloak(mockKc);
          setAuthenticated(true);
          setToken("mock-token");
          (window as unknown as { keycloak?: Keycloak }).keycloak = mockKc;
          
          // Execute the normal successful login checks
          try {
            const result = await authService.ensureAccount(mockEmail);
            if (result.organizations && result.organizations.length > 1) {
              let selectedOrg = getPersistedOrganization(result.organizations);
              if (!selectedOrg) {
                selectedOrg = await new Promise<{ id: string; name: string }>((resolve) => {
                  setResolvePendingOrg(() => resolve);
                  setPendingOrganizations(result.organizations!);
                  setInitialized(true);
                });
              }
              useSettingsStore.getState().setLegalEntity(selectedOrg.id, selectedOrg.name);
              setPendingOrganizations([]);
              setInitialized(false);
            }
            const profile = await profileService.get(mockEmail);
            setProfileCompleted(profile.profileCompleted);
          } catch (err) {
            console.warn("Mock Account provision check failed. Rejecting login:", err);
            setAuthenticated(false);
            setToken(undefined);
            alert("Access Denied: Mock User setup is incomplete.");
            await new Promise(() => { });
            return;
          }
          setInitialized(true);
          return;
        }

        // On localhost, skip the silent SSO iframe to avoid CSP/CORS issues
        // (Keycloak's frame-ancestors policy won't include localhost)
        const isLocalDev =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1" ||
          window.location.hostname === "relative-beads-watershed-hopefully.trycloudflare.com";

        const auth = await kc.init({
          onLoad: "check-sso",
          pkceMethod: "S256",
          ...(!isLocalDev && {
            silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          }),
          silentCheckSsoFallback: false,
          checkLoginIframe: false,
        });

        // Apply the account's theme as soon as we know who signed in, so the
        // loading screen and org picker already render in it.
        if (auth && kc.tokenParsed?.email) useThemeStore.getState().setAccount(kc.tokenParsed.email);

        setKeycloak(kc);
        setAuthenticated(auth);
        (window as unknown as { keycloak?: Keycloak }).keycloak = kc;

        if (auth) {
          setToken(kc.token);

          // We MUST await ensureAccount to verify cross-app users before rendering the application.
          // If we don't block here, unauthorized users will briefly see the Dashboard (with failed API calls)
          // before being kicked out by the 403.
          const email = kc.tokenParsed?.email;
          if (email) {
            try {
              const result = await authService.ensureAccount(email);
              if (result.organizations && result.organizations.length > 1) {
                let selectedOrg = getPersistedOrganization(result.organizations);
                if (!selectedOrg) {
                  selectedOrg = await new Promise<{ id: string; name: string }>((resolve) => {
                    setResolvePendingOrg(() => resolve);
                    setPendingOrganizations(result.organizations!);
                    setInitialized(true);
                  });
                }
                useSettingsStore.getState().setLegalEntity(selectedOrg.id, selectedOrg.name);
                setPendingOrganizations([]);
                setInitialized(false);
              }
              if (result.justProvisioned) {
                console.log("Account just provisioned, forcing token refresh to fetch new roles...");
                await kc.updateToken(-1);
                setToken(kc.token);
              }
              const profile = await profileService.get(email);
              setProfileCompleted(profile.profileCompleted);
            } catch (err: any) {
              console.warn("Account provision check failed. Rejecting login:", err);

              // We must strictly block access if ensureAccount fails (whether it's 403, 500, or anything else).
              // If we allow them in, they will access the Dashboard without a provisioned Legal Entity.
              setAuthenticated(false);
              setToken(undefined);

              alert("Access Denied: You must register for Bridge Atlas before logging in, or your account setup is incomplete.");
              await logout("/auth/signup");

              // Stall initialization forever to prevent the React app from rendering
              await new Promise(() => { });
              return;
            }
          }

          const interval = window.setInterval(async () => {
            try {
              const refreshed = await kc.updateToken(70);
              if (refreshed) setToken(kc.token);
            } catch (err) {
              console.warn("Proactive token refresh failed:", err);
              handleSessionExpired();
            }
          }, 30_000);
          cleanup = () => window.clearInterval(interval);
        }
      } catch (err) {
        console.warn("Keycloak initialization failed (SSO check). Continuing unauthenticated:", err);
        // Still set the instance so ProtectedRoute can trigger login redirect
        setKeycloak(kc);
        (window as unknown as { keycloak?: Keycloak }).keycloak = kc;
      } finally {
        setInitialized(true);
      }
    };

    void initKeycloak();
    return () => cleanup?.();
  }, []);

  const logout = async (redirectPath?: string) => {
    setToken(undefined);
    // Clear Service Workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
    }

    // Clear Cache Storage
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
      }
    }

    // Clear all IndexedDB databases
    if ('indexedDB' in window) {
      if (indexedDB.databases) {
        // Modern browsers
        const dbs = await indexedDB.databases();
        for (const db of dbs) {
          if (db.name) indexedDB.deleteDatabase(db.name);
        }
      } else {
        // Fallback: try to delete known DBs (if any)
        // Add your DB names here if needed
      }
    }

    // Use the global instance to avoid stale closure if called during early init
    // Clear local storage for settings so the next login starts fresh
    localStorage.removeItem("atlas-settings");
    // Forget whose theme to pre-paint so the next sign-in starts in light mode.
    // The current page keeps its theme — switching to light here would flash
    // before the redirect.
    useThemeStore.getState().forgetAccount();
    keycloakInstance.logout({ redirectUri: window.location.origin + (redirectPath || "") });
  };

  const contextValue = useMemo<KeycloakContextType>(
    () => ({ authenticated, keycloak, logout, token, profileCompleted }),
    [authenticated, keycloak, token, profileCompleted],
  );

  if (!initialized) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-bridge-bg">
        <div className="w-12 h-12 border-4 border-bridge-accent/20 border-t-bridge-accent rounded-full animate-spin mb-4" />
        <p className="text-sm font-medium text-bridge-muted">Initializing...</p>
      </div>
    );
  }

  if (pendingOrganizations.length > 0 && resolvePendingOrg) {
    return (
      <OrganizationSelectorModal
        organizations={pendingOrganizations}
        onSelect={(id) => {
          const org = pendingOrganizations.find((o) => o.id === id);
          if (org) resolvePendingOrg(org);
        }}
      />
    );
  }

  // Allow public routes (e.g. /auth/signup) to render even if not authenticated
  const isPublicRoute = window.location.pathname.startsWith("/auth");

  if (!authenticated && !isPublicRoute) {
    // If not authenticated and not a public route, we let the router handle the redirect
    // or we could trigger login here. For now, we'll just render the children
    // and let ProtectedRoute handle it.
  }

  return <KeycloakContext.Provider value={contextValue}>{children}</KeycloakContext.Provider>;
};
